import {
  AVProject,
  NewProfessional,
  ProfessionalDetail,
  ProfessionalProject,
} from './types'

export const reverseDate = (date: string | null) => {
  if (!date) return ''
  return date.split('T')[0].split('-').reverse().join('-')
}

export const CUTOFF_POINT_LABELS = {
  LAST_CALENDAR_DAY_OF_MONTH: 'Last calendar day of month',
  LAST_FRIDAY_OF_MONTH: 'Last Friday of month',
} as const

export const DEFAULT_NEW_PROFESSIONAL: NewProfessional = {
  CodeAV: '',
  FirstName: '',
  LastName: '',
  Email: '',
}

export const createDefaultProject = (professionalId: number): AVProject => ({
  ID: 0,
  AVProfessionalID: professionalId,
  ProjectCode: '',
  HoursCostCode: 0,
  ExpensesCostCode: null,
  ProjectType: 'Purchase',
  ExpensesReimbursed: null,
  TravelReimbursed: null,
  ExpensesDeductible: null,
  TravelDeductible: null,
  TravelExpensesDeductible: null,
  PaidLeave: null,
  WorkedFromHome: null,
  UnitType: null,
  StartDate: new Date().toISOString().slice(0, 10),
  EndDate: null,
  CutoffPoint: null,
  ApproverID: null,
  Approvers: [],
  ApproverName: null,
  MagnoEntity: 'MIR',
  Supplier: null,
  MaxDeductibleTravel: null,
  ClientCode: null,
  ExactSupplierCode: null,
  MIPProjectCode: null,
})

const normalizeProjectOptions = (project: AVProject): AVProject => ({
  ...project,
  WorkedFromHome:
    project.Supplier === 'MIR' && project.WorkedFromHome === null
      ? false
      : project.WorkedFromHome,
})

export const normalizeProjectDates = (project: AVProject): AVProject =>
  normalizeProjectOptions({
    ...project,
    StartDate: project.StartDate ? project.StartDate.slice(0, 10) : '',
    EndDate: project.EndDate ? project.EndDate.slice(0, 10) : null,
  })

export const changeProjectType = (
  project: AVProject,
  projectType: AVProject['ProjectType']
): AVProject => ({
  ...project,
  ProjectType: projectType,
  UnitType: null,
  ExpensesReimbursed: null,
  TravelReimbursed: null,
  ExpensesDeductible: null,
  TravelDeductible: null,
  TravelExpensesDeductible: null,
  PaidLeave: null,
  WorkedFromHome: null,
  CutoffPoint: null,
  ApproverID: null,
  Approvers: [],
  MagnoEntity: 'MIR',
  Supplier: null,
  MaxDeductibleTravel: null,
  ClientCode: null,
  ExactSupplierCode: null,
})

export const changeSupplier = (
  project: AVProject,
  supplier: AVProject['Supplier']
): AVProject => ({
  ...project,
  ExpensesReimbursed: false,
  TravelReimbursed: false,
  ExpensesDeductible: false,
  TravelDeductible: false,
  TravelExpensesDeductible: false,
  PaidLeave: false,
  WorkedFromHome: false,
  Supplier: supplier,
  MaxDeductibleTravel: null,
  ExactSupplierCode: null,
})

export const validateProjectInputs = (project: AVProject): string | undefined => {
  if (!project.ProjectCode) {
    return 'Please enter a project code'
  }
  if (project.ProjectType === 'Sales') {
    if (project.UnitType === null) {
      return 'Please choose a unit type'
    }
    if (project.Supplier === 'MIR') {
      if (project.ExpensesReimbursed === null) {
        return 'Please choose if expenses are reimbursed'
      }
      if (project.TravelReimbursed === null) {
        return 'Please choose if travel (km) is reimbursed'
      }
      if (project.ExpensesDeductible === null) {
        return 'Please choose if other expenses are deductible'
      }
      if (project.TravelDeductible === null) {
        return 'Please choose if travel (km) is deductible'
      }
      if (project.TravelExpensesDeductible === null) {
        return 'Please choose if travel expenses are deductible'
      }
      if (project.PaidLeave === null) {
        return 'Please choose if paid leave is enabled'
      }
      if (project.WorkedFromHome === null) {
        return 'Please choose if worked from home is enabled'
      }
    }
    if (project.CutoffPoint === null) {
      return 'Please choose a cutoff point'
    }
    if (project.Supplier === null) {
      return 'Please choose a supplier'
    }
  }

  return undefined
}

const mapProfessionalProjectToProject = (
  project: ProfessionalProject
): AVProject =>
  normalizeProjectOptions({
  ID: project.PRJID,
  AVProfessionalID: project.ID,
  ProjectCode: project.ProjectCode,
  HoursCostCode: project.HoursCostCode,
  ExpensesCostCode: project.ExpensesCostCode,
  ProjectType: project.ProjectType,
  ExpensesReimbursed: project.ExpensesReimbursed,
  TravelReimbursed: project.TravelReimbursed,
  ExpensesDeductible: project.ExpensesDeductible,
  TravelDeductible: project.TravelDeductible,
  TravelExpensesDeductible: project.TravelExpensesDeductible,
  PaidLeave: project.PaidLeave,
  WorkedFromHome: project.WorkedFromHome,
  UnitType: project.UnitType,
  StartDate: project.StartDate,
  EndDate: project.EndDate,
  CutoffPoint: project.CutoffPoint,
  ApproverID: project.ApproverID,
  ApproverName: project.ApproverName,
  Approvers: project.Approvers || [],
  MagnoEntity: project.MagnoEntity,
  Supplier: project.Supplier,
  MaxDeductibleTravel: project.MaxDeductibleTravel,
  ClientCode: project.ClientCode,
  ExactSupplierCode: project.ExactSupplierCode,
  MIPProjectCode: project.MIPProjectCode,
  })

export const mapProjects = (
  professionalProjects: ProfessionalProject[]
): AVProject[] => {
  if (!professionalProjects.length || !professionalProjects[0].PRJID) {
    return []
  }

  return professionalProjects.map(mapProfessionalProjectToProject)
}

export const mapProfessionalDetail = (
  professionalProjects?: ProfessionalProject[]
): ProfessionalDetail | undefined => {
  if (!professionalProjects?.length) {
    return undefined
  }

  const professional = professionalProjects[0]
  return {
    ID: professional.ID,
    Email: professional.Email,
    CodeAV: professional.CodeAV,
    FirstName: professional.FirstName,
    LastName: professional.LastName,
    UserID: professional.UserID,
    projects: mapProjects(professionalProjects),
  }
}

export const mapProfessionalProjectToDetail = (
  professionalProject: ProfessionalProject
): ProfessionalDetail => ({
  ID: professionalProject.ID,
  Email: professionalProject.Email,
  CodeAV: professionalProject.CodeAV,
  FirstName: professionalProject.FirstName,
  LastName: professionalProject.LastName,
  UserID: professionalProject.UserID,
  projects: professionalProject.PRJID
    ? [mapProfessionalProjectToProject(professionalProject)]
    : [],
})
